import React, { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  useGetBonus25Methods,
  useGetBonusTypes,
  useGetCLAOptions
} from 'queries';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { PremiumPayFlagsEnum } from '../../types';
import { PremiumPayFormValues } from './types';

import { isSvcTypeBonus, mapToOptions } from '../../utils/utils';
import { convertPayloadForSave } from './utils/utils';

import { PREMIUM_PAYS } from 'RestAPIs/endpoints';
import { useSaveResource } from 'RestAPIs/useSaveResource';

import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  ControlledAutoComplete,
  ControlledCheckbox,
  ControlledDatePicker,
  ControlledSelect,
  ControlledTimePicker
} from 'components/shared';
import TextField from 'components/shared/ControlledFields/ControlledTextField';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import { schema } from './schema';

type CreateEditFunctionFormProps = {
  handleClose: () => void;
  rowData?: PremiumPayFormValues;
};

const PremiumPayForm: React.FC<CreateEditFunctionFormProps> = ({
  handleClose,
  rowData
}) => {
  const { t } = useTranslation(['common']);
  const queryClient = useQueryClient();

  const { data: premiumPayTypes } = useGetCLAOptions();
  const { data: bonusTypes } = useGetBonusTypes();
  const { data: bonus25Methods } = useGetBonus25Methods();

  const { mutateAsync, isLoading } = useSaveResource({
    url: PREMIUM_PAYS,
    id: rowData?.claBonusId,
    invalidateQueryKey: ['premiumPays']
  });

  const methods = useForm<PremiumPayFormValues>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    defaultValues: rowData
      ? { ...rowData, bonusTypeSvc: undefined }
      : schema.cast({}),
    resolver: yupResolver(schema)
  });

  const { control, handleSubmit, watch } = methods;

  const { timeFrom } = watch();
  const baseDate = timeFrom?.startOf('day');

  const onSubmit = (data) => {
    const payload = convertPayloadForSave(data, premiumPayTypes);

    mutateAsync(payload)
      .then(async () => {
        await queryClient.invalidateQueries(['premiumPay', data?.claBonusId]);
      })
      .then(handleClose);
  };

  useEffect(() => {
    if (rowData) {
      methods.reset({
        ...rowData,
        bonusTypeSvc: mapToOptions(bonusTypes, 'code', 'name').find(
          ({ value }) => value === rowData.bonusTypeSvc?.label
        )
      });
    }
  }, [rowData, bonusTypes]);

  return (
    <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
      <MultiStepForm.Section
        title={t('premiumPays.form.sections.base')}
        validateFields={['claCode', 'bonusTypeSvc', 'columnNr', 'percentage']}
      >
        <ControlledSelect
          name="claCode"
          control={control}
          variant="outlined"
          label={t('premiumPays.grid.claId')}
          options={mapToOptions(premiumPayTypes, 'code', 'code')}
        />

        <ControlledAutoComplete
          name="bonusTypeSvc"
          control={control}
          variant="outlined"
          label={t('premiumPays.grid.bonusTypeSvc')}
          options={mapToOptions(bonusTypes, 'code', 'name')}
          idProperty="value"
          optionLabelProp="label"
          fullWidth
        />

        <Box display="flex" gap={2.5}>
          <TextField
            name="columnNr"
            type="number"
            control={control}
            variant="outlined"
            label={t('premiumPays.grid.columnNr')}
          />
          <TextField
            name="percentage"
            type="number"
            control={control}
            variant="outlined"
            label={t('premiumPays.grid.percentage')}
            inputProps={{
              step: 0.001
            }}
          />
        </Box>

        <Box display="flex" gap={2.5}>
          <ControlledTimePicker
            name="timeFrom"
            control={control}
            variant="outlined"
            label={t('premiumPays.grid.timeFrom')}
            baseDate={baseDate}
          />
          <ControlledTimePicker
            name="timeTo"
            control={control}
            variant="outlined"
            label={t('premiumPays.grid.timeTo')}
            baseDate={baseDate}
          />
        </Box>

        {isSvcTypeBonus(methods.getValues(['bonusTypeSvc'])?.[0]?.label) && (
          <>
            <Box gap={2.5}>
              <ControlledSelect
                name="bonus25Method"
                control={control}
                variant="outlined"
                label={t('premiumPays.grid.bonus25Method')}
                options={mapToOptions(bonus25Methods, 'code', 'name')}
              />
            </Box>
            <Box display="flex" gap={2.5}>
              <TextField
                name="maxHoursPerDayrange"
                type="number"
                control={control}
                variant="outlined"
                label={t('premiumPays.grid.maxHoursPerDayrange')}
              />
              <TextField
                name="dayrange"
                type="number"
                control={control}
                variant="outlined"
                label={t('premiumPays.grid.dayrange')}
              />
            </Box>
          </>
        )}
      </MultiStepForm.Section>

      <MultiStepForm.Section
        title={t('premiumPays.grid.scAll')}
        validateFields={['scPri']}
      >
        <Box display="flex" gap={2.5}>
          <TextField
            name="scPri"
            type="text"
            control={control}
            variant="outlined"
            label={t('premiumPays.grid.scPri')}
          />
          <TextField
            name="scSec"
            type="text"
            control={control}
            variant="outlined"
            label={t('premiumPays.grid.scSec')}
          />
        </Box>
      </MultiStepForm.Section>

      <MultiStepForm.Section title={t('premiumPays.form.sections.days')}>
        <Grid container>
          <Grid item xs={6}>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_MONDAY}]`}
              label={t('weekday.monday')}
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_TUESDAY}]`}
              label={t('weekday.tuesday')}
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_WEDNESDAY}]`}
              label={t('weekday.wednesday')}
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_THURSDAY}]`}
              label={t('weekday.thursday')}
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_FRIDAY}]`}
              label={t('weekday.friday')}
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_SATURDAY}]`}
              label={t('weekday.saturday')}
            />
          </Grid>
          <Grid item xs={5}>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_SUNDAY}]`}
              label={t('weekday.sunday')}
            />
          </Grid>
        </Grid>
      </MultiStepForm.Section>

      <MultiStepForm.Section title={t('premiumPays.form.sections.flags')}>
        <Grid container>
          <Grid item xs={6}>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_HOLIDAY}]`}
              label={t('premiumPays.grid.flags.DAY_HOLIDAY')}
            />
          </Grid>

          <Grid item xs={6}>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_HOLIDAY_SAT}]`}
              label={t('premiumPays.grid.flags.DAY_HOLIDAY_SAT')}
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_HOLIDAY_SUN}]`}
              label={t('premiumPays.grid.flags.DAY_HOLIDAY_SUN')}
            />
          </Grid>

          <Grid item xs={6}>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_SPECIAL}]`}
              label={t('premiumPays.grid.flags.DAY_SPECIAL')}
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.TIME_FROM_THR}]`}
              label={t('premiumPays.grid.flags.TIME_FROM_THR')}
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.TIME_TO_THR}]`}
              label={t('premiumPays.grid.flags.TIME_TO_THR')}
            />
          </Grid>
        </Grid>
        <Box display="flex" gap={2.5}>
          {methods.getValues(['flags'])?.[0]?.['TIME_FROM_THR'] && (
            <Grid item xs={6}>
              <ControlledTimePicker
                name="timeFromThr"
                control={control}
                variant="outlined"
                label={t('premiumPays.grid.timeFromThr')}
              />
            </Grid>
          )}

          {methods.getValues(['flags'])?.[0]?.['TIME_TO_THR'] && (
            <Grid item xs={6}>
              <ControlledTimePicker
                name="timeToThr"
                control={control}
                variant="outlined"
                label={t('premiumPays.grid.timeToThr')}
              />
            </Grid>
          )}
        </Box>
      </MultiStepForm.Section>

      <MultiStepForm.Section
        title={t('premiumPays.form.sections.valid')}
        validateFields={['valid.from', 'valid.to']}
      >
        <Box display="flex" gap={2.5}>
          <ControlledDatePicker
            control={control}
            name="valid.from"
            variant="outlined"
            label={t('validFrom')}
          />
          <ControlledDatePicker
            control={control}
            name="valid.to"
            variant="outlined"
            label={t('validTo')}
          />
        </Box>
      </MultiStepForm.Section>
      <MultiStepForm.Actions>
        <Box display="flex" ml="auto">
          <LoadingButton
            loading={isLoading}
            type="submit"
            variant="contained"
            autoFocus
            color="dark"
          >
            {t('save')}
          </LoadingButton>
        </Box>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};

export default PremiumPayForm;
